import { useMemo, useState } from "react";
import { useQuery } from "react-query";

import { getComments, Comment } from "@/api";
import { useResponsiveness, useTranslate, useUser } from "@/providers/context";
import { DRAWER_COMMENT } from "@/const";
import { CommentsProps } from "../Comments";
import { useAppProperties } from "@/hooks";

const countComments: (c: Comment) => number = (comment) =>
  comment.Responses.flatMap(countComments).reduce((acc, val) => acc + val, 1);

export const useComments = ({
  itemId,
  dataSource,
  sourceId,
  liveEventStartDate,
  liveEventEnded,
}: CommentsProps) => {
  const { uid, user_name } = useUser();
  const { openDrawer } = useResponsiveness();
  const {
    data: properties,
    showCommentsOnItems,
    showCommentsOnLiveStreams,
    commentsLabel,
  } = useAppProperties();
  const { translate } = useTranslate();

  const commentsBelowPlayer =
    properties?.WebItemDetailsDesign === "CommentsBelowPlayer";

  const [pendingComment, setPendingComment] = useState<
    (Comment & { ParentCommentId?: number }) | null
  >(null);

  const isOpen = commentsBelowPlayer || openDrawer === DRAWER_COMMENT;
  const isLiveEvent = !!liveEventStartDate || !!liveEventEnded; // Live event of any state, e.g. scheduled, live or ended
  const autoRefresh = isOpen && isLiveEvent;

  const disabled =
    liveEventStartDate && !liveEventEnded
      ? !showCommentsOnLiveStreams
      : !showCommentsOnItems;

  const query = useQuery(
    ["comments", itemId],
    () => {
      return getComments({ itemId, sourceId, dataSource });
    },
    {
      enabled: !!sourceId && !!itemId && !!dataSource && !disabled,
      refetchInterval: autoRefresh ? 10000 : false, // Refetch every 10 seconds, used for live streams
    },
  );

  const combinedData = useMemo(() => {
    if (!uid || !user_name || disabled) {
      return null;
    }

    if (!query.data || !pendingComment) {
      return query.data;
    }
    const isResponse = pendingComment.ParentCommentId !== undefined;
    if (isResponse) {
      return query.data.map((c) =>
        c.SourceCommentId === pendingComment.ParentCommentId
          ? { ...c, Responses: [...c.Responses, pendingComment] }
          : c,
      );
    }
    return [...query.data, pendingComment];
  }, [query.data, pendingComment, uid, user_name, disabled]);

  const count = useMemo(
    () =>
      combinedData
        ? combinedData.flatMap(countComments).reduce((acc, val) => acc + val, 0)
        : 0,
    [combinedData],
  );

  const label = translate(commentsLabel);
  const labelWithCount = combinedData ? `${label} (${count ?? 0})` : label;

  const sortedComments = useMemo(() => {
    return combinedData?.sort((a, b) =>
      b.CommentDate.localeCompare(a.CommentDate),
    );
  }, [combinedData]);

  return {
    ...query,
    pendingComment,
    setPendingComment,
    sortedComments,
    count,
    label,
    labelWithCount,
    disabled,
  };
};
